import { useState, useEffect } from "react";
import { X, Crown, CheckCircle, XCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import StripePaymentWrapper from "@/components/payment/StripePaymentWrapper";
import CaktoPaymentWrapper from "@/components/payment/CaktoPaymentWrapper";
import SuccessLoader from "@/components/payment/SuccessLoader";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface LiveStreamPaymentModalProps {
 open: boolean;
 onClose: () => void;
 onSuccess: () => void;
 streamId: string;
 streamTitle: string;
 price: number;
 userEmail: string;
}

const LiveStreamPaymentModal = ({
 open,
 onClose,
 onSuccess,
 streamId,
 streamTitle,
 price,
 userEmail,
}: LiveStreamPaymentModalProps) => {
 const [processing, setProcessing] = useState(false);
 const [result, setResult] = useState<"approved" | "failed" | null>(null);
 const [detailedError, setDetailedError] = useState<string>("");
 const [paymentProvider, setPaymentProvider] = useState<"stripe" | "cakto">("stripe");
 
 useEffect(() => {
 supabase.from("site_settings" as any).select("*").then(({ data }) => {
 if (data) {
 const active = (data as any[]).find((s: any) => s.key === "payment_provider")?.value || "stripe";
 setPaymentProvider(active as any);
 }
 });
 }, []);

 const handleSuccess = () => {
 setResult("approved");
 };

 const handleSuccessComplete = () => {
 onSuccess();
 onClose();
 };

 const handleError = (errorMsg: string) => {
 setResult("failed");
 setDetailedError(errorMsg);
 toast.error(errorMsg);
 };

 if (!open) return null;

 return (
 <AnimatePresence>
 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/10 p-4"
 onClick={(e) => { if (e.target === e.currentTarget && !processing) onClose(); }}
 >
 {result === "approved" && (
 <SuccessLoader 
 type="video" 
 onComplete={handleSuccessComplete} 
 />
 )}
 
 <motion.div
 initial={{ scale: 0.9, opacity: 0 }}
 animate={{ scale: 1, opacity: 1 }}
 exit={{ scale: 0.9, opacity: 0 }}
 className="bg-card border border-border rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl"
 >
 {/* Header */}
 <div className="flex items-center justify-between p-5 border-b border-border">
 <div className="flex items-center gap-2">
 <Crown className="w-5 h-5 text-primary" />
 <h2 className="font-display text-lg text-foreground">Acesso à transmissão</h2>
 </div>
 <button onClick={onClose} disabled={processing} className="text-muted-foreground hover:text-foreground">
 <X className="w-5 h-5" />
 </button>
 </div>

 <div className="p-5">
 {result === "failed" ? (
 <div className="flex flex-col items-center py-8 gap-3 text-center">
 <XCircle className="w-16 h-16 text-destructive mx-auto" />
 <h3 className="text-xl font-bold">Pagamento recusado</h3>
 <p className="text-sm text-muted-foreground">
 Tente novamente ou use outro método de pagamento.
 </p>
 {detailedError && (
 <p className="text-xs text-red-500 font-mono mt-2 p-2 bg-red-100 rounded">
 {detailedError}
 </p>
 )}
 <Button className="w-full mt-4" onClick={() => setResult(null)}>
 Tentar Novamente
 </Button>
 </div>
 ) : result !== "approved" && (
 <>
 <div className="bg-secondary rounded-lg p-4 mb-5 text-center">
 <p className="text-sm text-muted-foreground">{streamTitle}</p>
 <p className="text-2xl font-bold text-foreground mt-1">
 R$ {price.toFixed(2).replace(".", ",")}
 </p>
 </div>
 {paymentProvider === "stripe" ? (
 <StripePaymentWrapper 
 streamId={streamId} 
 onSuccess={handleSuccess} 
 onError={handleError} 
 />
 ) : (
 <CaktoPaymentWrapper 
 onSuccess={handleSuccess} 
 onError={handleError} 
 />
 )}
 </>
 )}
 </div>
 </motion.div>
 </motion.div>
 </AnimatePresence>
 );
};

export default LiveStreamPaymentModal;
